import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiTwitter, FiMail, FiSend, FiFacebook, FiPhone } from 'react-icons/fi';
import { SiX } from 'react-icons/si';
import { personalInfo, socialLinks } from '../data/portfolioData';
import { trackEvent } from '../utils/analytics';

const iconMap = {
  github: FiGithub,
  linkedin: FiLinkedin,
  twitter: FiTwitter,
  email: FiMail,
  facebook: FiFacebook,
  x: SiX,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('contact_form_submit', { form_location: 'contact_section' });
    alert(`Thanks ${formData.name}! Your message has been sent.`);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="contact">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <motion.h2 className="section-comment" variants={itemVariants}>
          {'// get-in-touch'}
        </motion.h2>
        <motion.p className="section-subtitle" variants={itemVariants}>
          Have a project in mind? Let's work together
        </motion.p>

        <div className="contact-content">
          <motion.form
            className="contact-form"
            onSubmit={handleSubmit}
            variants={itemVariants}
          >
            <div className="form-group">
              <label className="form-label">
                <span className="line-number">1</span>
                <span className="syntax-keyword">const</span>{' '}
                <span className="syntax-variable">name</span>{' = '}
              </label>
              <input
                className="form-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="line-number">2</span>
                <span className="syntax-keyword">const</span>{' '}
                <span className="syntax-variable">email</span>{' = '}
              </label>
              <input
                className="form-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="line-number">3</span>
                <span className="syntax-keyword">const</span>{' '}
                <span className="syntax-variable">message</span>{' = '}
              </label>
              <textarea
                className="form-input"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your project..."
                rows={5}
                required
              />
            </div>

            <motion.button
              type="submit"
              className="btn-primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{'// send message'}</span>
              <FiSend />
            </motion.button>
          </motion.form>

          <motion.div className="contact-info" variants={itemVariants}>
            <div className="contact-status">
              {personalInfo.availableForHire && <span className="green-dot" />}
              <span>
                {personalInfo.availableForHire
                  ? 'Available for new projects'
                  : 'Currently unavailable'}
              </span>
            </div>

            <motion.a
              href={`mailto:${personalInfo.email}`}
              className="contact-detail"
              onClick={() => trackEvent('contact_click', { method: 'email' })}
              style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
              whileHover={{ scale: 1.02, borderColor: 'var(--accent-color)' }}
              whileTap={{ scale: 0.98 }}
            >
              <FiMail />
              <span>{personalInfo.email}</span>
            </motion.a>

            {personalInfo.phone && (
              <motion.a
                href={`https://wa.me/${personalInfo.phone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-detail"
                onClick={() => trackEvent('contact_click', { method: 'whatsapp' })}
                style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                whileHover={{ scale: 1.02, borderColor: 'var(--accent-color)' }}
                whileTap={{ scale: 0.98 }}
              >
                <FiPhone />
                <span>{personalInfo.phone}</span>
              </motion.a>
            )}

            <div className="contact-detail">
              <span>{personalInfo.location}</span>
            </div>

            <div className="social-links">
              {socialLinks.map((link) => {
                const Icon = iconMap[link.icon] || FiGithub;
                return (
                  <motion.a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    onClick={() =>
                      trackEvent('social_click', {
                        network: link.name,
                        location: 'contact_section',
                      })
                    }
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon />
                  </motion.a>
                );
              })}
            </div>

            <div className="contact-snippet">
              <div className="code-line">
                <span className="line-number">1</span>
                <span className="line-content">
                  <span className="syntax-keyword">await</span>{' '}
                  <span className="syntax-function">fetch</span>(
                  <span className="syntax-string">'/api/hire-me'</span>)
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Contact;
